import { GetServerSideProps } from "next"
import Head from "next/head"
import { DriverListClient } from "@/components/drivers/driver-list-client"
import { getDriversPage } from "@/lib/drivers/fetch"
import {
  DriverProfile,
  FilterStats,
  SearchParamsType,
} from "@/lib/drivers/types"
import PageNav from "@/storyblok/sections/PageNav"
import Footer from "@/storyblok/static/Footer"
import LocalPageHeader from "@/storyblok/sections/LocalPageHeader"
import Breadcrumb from "@/components/breadcrumbs"
import { ReviewsContextProvider } from "@/context/ReviewsContext"
import {
  fromURLParam,
  toURLParam,
  cleanURLParams,
  URL_PARAMS,
  recordToURLParams,
  fromURLFilterValue,
  URLParamValues,
} from "@/lib/drivers/url-params"

const ITEMS_PER_PAGE = 12

interface DriversPageProps {
  profiles: DriverProfile[]
  filterStats: FilterStats
  currentPage: number
  totalPages: number
  searchParams: Record<string, string | string[] | undefined>
  totalDrivers: number
  itemsPerPage: number
}

export const getServerSideProps: GetServerSideProps<DriversPageProps> = async ({
  query,
}) => {
  try {
    // Convert query to URLSearchParams and clean them
    const params = new URLSearchParams()
    Object.entries(query).forEach(([key, value]) => {
      if (value && typeof value === "string" && value.trim() !== "") {
        // Convert the key to kebab-case before setting
        const urlKey = toURLParam(key)
        params.set(urlKey, value)
      }
    })
    const cleanParams = cleanURLParams(params)

    // Get the page number from the clean parameters
    const page = Number(cleanParams.get("page")) || 1

    // Convert URL parameters to internal keys for search params
    const searchParams: SearchParamsType = {
      verifications:
        cleanParams
          .get("verifications")
          ?.split(",")
          .map((v) => fromURLFilterValue(v))
          .filter(Boolean)
          .join(",") || undefined,
      shipmentTypes:
        cleanParams
          .get("shipment-types")
          ?.split(",")
          .map((v) => fromURLFilterValue(v))
          .filter(Boolean)
          .join(",") || undefined,
      rating:
        cleanParams
          .get("rating")
          ?.split(",")
          .map((v) => fromURLFilterValue(v))
          .filter(Boolean)
          .join(",") || undefined,
      experience:
        cleanParams
          .get("experience")
          ?.split(",")
          .map((v) => fromURLFilterValue(v))
          .filter(Boolean)
          .join(",") || undefined,
      wonShipments:
        cleanParams
          .get("won-shipments")
          ?.split(",")
          .map((v) => fromURLFilterValue(v))
          .filter(Boolean)
          .join(",") || undefined,
    }

    const {
      profiles,
      filterStats,
      totalPages,
      totalDrivers,
    } = await getDriversPage(page, ITEMS_PER_PAGE, {
      ...searchParams,
      verifications: searchParams.verifications?.split(",") || [],
      shipmentTypes: searchParams.shipmentTypes?.split(","),
      rating: searchParams.rating ? Number(searchParams.rating) : undefined,
    })

    // Convert clean parameters to a record for the response
    const cleanParamsRecord: Record<string, string> = {}
    cleanParams.forEach((value, key) => {
      // Convert the key back to camelCase for internal use
      const internalKey = fromURLParam(key)
      if (key === "verifications") {
        // Convert verification values from kebab-case to camelCase
        cleanParamsRecord[internalKey] = value
          .split(",")
          .map((v) => fromURLFilterValue(v))
          .join(",")
      } else if (key === "won-shipments") {
        // Convert won-shipments value from kebab-case to camelCase
        cleanParamsRecord[internalKey] = fromURLFilterValue(value)
      } else {
        cleanParamsRecord[internalKey] = value
      }
    })

    return {
      props: {
        profiles,
        filterStats,
        currentPage: page,
        totalPages,
        searchParams: cleanParamsRecord,
        totalDrivers,
        itemsPerPage: ITEMS_PER_PAGE,
      },
    }
  } catch (error) {
    console.error("Error in getServerSideProps:", error)

    // Convert query to URLSearchParams and clean them, even in error case
    const params = new URLSearchParams()
    Object.entries(query).forEach(([key, value]) => {
      if (value && typeof value === "string" && value.trim() !== "") {
        const urlKey = toURLParam(key)
        params.set(urlKey, value)
      }
    })
    const cleanParams = cleanURLParams(params)

    // Convert clean parameters to a record
    const cleanParamsRecord: Record<string, string> = {}
    cleanParams.forEach((value, key) => {
      const internalKey = fromURLParam(key)
      cleanParamsRecord[internalKey] = value
    })

    return {
      props: {
        profiles: [],
        filterStats: {
          verifications: {
            backgroundCheck: 0,
            insurance: 0,
            identity: 0,
            usdot: 0,
            usda: 0,
            manual: 0,
          },
          shipmentTypes: {},
          ratings: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          experience: { new: 0, intermediate: 0, expert: 0 },
          wonShipments: { lessThan10: 0, between10And50: 0, moreThan50: 0 },
        },
        currentPage: 1,
        totalPages: 1,
        searchParams: cleanParamsRecord,
        totalDrivers: 0,
        itemsPerPage: ITEMS_PER_PAGE,
      },
    }
  }
}

export default function DriversPage({
  profiles,
  filterStats,
  currentPage,
  totalPages,
  searchParams,
  totalDrivers,
  itemsPerPage,
}: DriversPageProps) {
  const baseUrl =
    process.env.NEXT_PUBLIC_SITE_URL || "https://citizenshipper.com"
  const currentUrl = new URL("/drivers", baseUrl)
  const canonicalUrl = new URL("/drivers", baseUrl)

  // Convert searchParams to URLSearchParams and clean them
  const params = recordToURLParams(searchParams)

  // Add clean parameters to URLs
  const validFilterParams = new URLSearchParams()
  params.forEach((value, key) => {
    // Only add valid filter parameters to the canonical URL
    if (
      Object.values(URL_PARAMS).includes(key as URLParamValues) &&
      key !== URL_PARAMS.page
    ) {
      validFilterParams.append(key, value)
    }
    // Add all parameters to the current URL
    currentUrl.searchParams.append(key, value)
  })

  // Sort the parameters alphabetically for consistency
  const sortedParams = new URLSearchParams(
    Array.from(validFilterParams.entries()).sort(),
  )
  sortedParams.forEach((value, key) => {
    canonicalUrl.searchParams.append(key, value)
  })

  let title = "Find Verified Transport Drivers | CitizenShipper"
  let heroHeading = "Find Verified Transport Drivers"
  let heroDescription =
    "Browse our selection of vetted transporters on CitizenShipper. Filter by experience, ratings, and specialties to find the perfect match for your shipping needs."

  // Customize title and hero content based on filters
  const filterDescriptions = {
    verifications: {
      backgroundCheck: {
        title: "Background Checked",
        description:
          "background checked transport drivers. Each driver has passed a comprehensive background check for your peace of mind",
      },
      insurance: {
        title: "Insurance Verified",
        description:
          "insurance verified drivers who maintain proper coverage for safe transport",
      },
      identity: {
        title: "Identity Verified",
        description:
          "identity verified drivers who have proven their credentials",
      },
      usdot: {
        title: "USDOT Certified",
        description:
          "USDOT certified drivers who meet Department of Transportation standards",
      },
      usda: {
        title: "USDA Certified",
        description:
          "USDA certified drivers specializing in pet transportation",
      },
      manual: {
        title: "Manual Verified",
        description:
          "manually verified drivers who have been reviewed by our team",
      },
    },
    experience: {
      new: {
        title: "New",
        description:
          "drivers with less than 6 months of experience, eager to prove themselves",
      },
      intermediate: {
        title: "Experienced",
        description:
          "drivers with 6 months to 2 years of experience in transportation",
      },
      expert: {
        title: "Expert",
        description:
          "highly experienced drivers with over 2 years of professional transport service",
      },
    },
    rating: {
      "4": {
        title: "Top Rated",
        description: "drivers with 4+ star ratings from verified customers",
      },
      "3": {
        title: "Well Rated",
        description: "drivers with 3+ star ratings from verified customers",
      },
    },
    wonShipments: {
      moreThan50: {
        title: "Veteran",
        description:
          "drivers who have successfully completed over 50 shipments",
      },
      between10And50: {
        title: "Established",
        description: "drivers who have completed between 10 and 50 shipments",
      },
      lessThan10: {
        title: "Emerging",
        description:
          "drivers who are building their reputation with up to 10 completed shipments",
      },
    },
  }

  // Build title and description based on active filters
  const activeFilters: string[] = []
  let mainDescription = ""

  // Check verifications
  if (
    searchParams.verifications &&
    typeof searchParams.verifications === "string"
  ) {
    const verifications = searchParams.verifications.split(",").filter(Boolean)
    verifications.forEach((v: string) => {
      // Convert URL parameter to internal format (e.g., "background-check" to "backgroundCheck")
      const internalKey = fromURLFilterValue(v)

      // Check if it's a valid verification type
      if (
        filterDescriptions.verifications[
          internalKey as keyof typeof filterDescriptions.verifications
        ]
      ) {
        const verification =
          filterDescriptions.verifications[
            internalKey as keyof typeof filterDescriptions.verifications
          ]
        activeFilters.push(verification.title)
        mainDescription = verification.description
      }
    })
  }

  // Check experience
  if (
    searchParams.experience &&
    typeof searchParams.experience === "string" &&
    ["new", "intermediate", "expert"].includes(searchParams.experience)
  ) {
    const experience =
      filterDescriptions.experience[
        searchParams.experience as keyof typeof filterDescriptions.experience
      ]
    if (experience) {
      activeFilters.push(experience.title)
      mainDescription = mainDescription || experience.description
    }
  }

  // Check rating
  if (
    searchParams.rating &&
    typeof searchParams.rating === "string" &&
    ["1", "2", "3", "4", "5"].includes(searchParams.rating)
  ) {
    const rating =
      filterDescriptions.rating[
        searchParams.rating as keyof typeof filterDescriptions.rating
      ]
    if (rating) {
      activeFilters.push(rating.title)
      mainDescription = mainDescription || rating.description
    }
  }

  // Check won shipments
  if (
    searchParams.wonShipments &&
    typeof searchParams.wonShipments === "string" &&
    ["lessThan10", "between10And50", "moreThan50"].includes(
      searchParams.wonShipments,
    )
  ) {
    const shipments =
      filterDescriptions.wonShipments[
        searchParams.wonShipments as keyof typeof filterDescriptions.wonShipments
      ]
    if (shipments) {
      activeFilters.push(shipments.title)
      mainDescription = mainDescription || shipments.description
    }
  }

  // Compose final title and description if there are active filters
  if (activeFilters.length > 0) {
    const filterTitle = activeFilters.join(" & ")
    title = `${filterTitle} Transport Drivers | CitizenShipper`
    heroHeading = `${filterTitle} Transport Drivers`
    heroDescription = `Find ${mainDescription}. Browse our selection of professional drivers and find the perfect match for your shipping needs.`
  }

  // Add page number to title if not on first page
  if (currentPage > 1) {
    title = `${title} - Page ${currentPage} of ${totalPages}`
  }

  const description = `Browse our selection of professional transport drivers. Filter by experience, ratings, and specialties to find the perfect match for your shipping needs.${
    currentPage > 1 ? ` Page ${currentPage} of ${totalPages}.` : ""
  }`

  const items = [
    { name: "Home", href: "/" },
    { name: "Drivers", href: "/drivers" },
  ]

  const headerProps = {
    heading: heroHeading,
    subheading: "Professional and Reliable Transportation Services",
    description: heroDescription,
    hasLongDescription: true,
    buttonLabel: "Get Transportation Quotes",
    buttonLink: "shipments/new",
    image: {
      alt: "transport drivers",
      filename:
        profiles[0]?.data.profilePhoto?.large ||
        "https://a-us.storyblok.com/f/1002522/716x860/f51afecb18/header-hero.png",
    },
    smBgImage: {
      alt: "transport drivers",
      filename:
        profiles[0]?.data.profilePhoto?.small ||
        "https://a-us.storyblok.com/f/1002522/375x714/1ab232cc02/header-hero-sm.png",
    },
    collage: profiles
      .slice(0, 4)
      .map((profile) => ({
        alt: profile.data.displayName,
        filename: profile.data.profilePhoto?.small || "",
        desktopFilename: profile.data.profilePhoto?.large || "",
      }))
      .filter((img) => img.filename),
    forceCollageOnMobile: true,
  }

  // Structured data for the drivers list page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": currentUrl.toString(),
    name: title,
    description,
    url: currentUrl.toString(),
    isPartOf: {
      "@type": "WebSite",
      "@id": baseUrl,
      name: "CitizenShipper",
      url: baseUrl,
    },
    about: {
      "@type": "Thing",
      name: "Transport Drivers",
      description:
        "Professional transport drivers offering shipping and transportation services",
    },
    numberOfItems: totalDrivers,
    itemListElement: profiles.map((profile, index) => ({
      "@type": "ListItem",
      position: (currentPage - 1) * ITEMS_PER_PAGE + index + 1,
      "@id": `${baseUrl}/drivers/${profile.data.id}`,
      url: `${baseUrl}/drivers/${profile.data.id}`,
      name: profile.data.displayName,
      description: `Professional transport driver with ${
        profile.experience?.years || 1
      } years of experience. ${
        profile.data.wonShipments
      } successful shipments.`,
    })),
  }

  // Add pagination structured data
  const paginationStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${currentUrl.toString()}#pagination`,
    name: `Transport Drivers - Page ${currentPage}`,
    description,
    isPartOf: {
      "@type": "CollectionPage",
      "@id": `${baseUrl}/drivers`,
      name: "CitizenShipper Drivers Directory",
    },
    url: currentUrl.toString(),
    pageStart: (currentPage - 1) * ITEMS_PER_PAGE + 1,
    pageEnd: Math.min(currentPage * ITEMS_PER_PAGE, totalDrivers),
    numberOfItems: totalDrivers,
    ...(currentPage > 1 && {
      hasPreviousPage: true,
      previousPage: new URL(`?page=${currentPage - 1}`, currentUrl).toString(),
    }),
    ...(currentPage < totalPages && {
      hasNextPage: true,
      nextPage: new URL(`?page=${currentPage + 1}`, currentUrl).toString(),
    }),
  }

  return (
    <ReviewsContextProvider>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />

        {/* Open Graph */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl.toString()} />
        {profiles[0]?.data.profilePhoto?.large && (
          <meta
            property="og:image"
            content={profiles[0].data.profilePhoto.large}
          />
        )}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {profiles[0]?.data.profilePhoto?.large && (
          <meta
            name="twitter:image"
            content={profiles[0].data.profilePhoto.large}
          />
        )}
        <link rel="canonical" href={canonicalUrl.toString()} />

        {/* Pagination Links */}
        {currentPage > 1 && (
          <link
            rel="prev"
            href={new URL(`?page=${currentPage - 1}`, currentUrl).toString()}
          />
        )}
        {currentPage < totalPages && (
          <link
            rel="next"
            href={new URL(`?page=${currentPage + 1}`, currentUrl).toString()}
          />
        )}

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([structuredData, paginationStructuredData]),
          }}
        />
      </Head>
      <PageNav noBlok buttonLabel="Get Transportation Quotes" />
      <section className="absolute md:relative z-50 top-20 md:pt-22 px-4 lg:px-10 lg:mx-auto lg:max-w-[1300px]">
        <div className="bg-white md:bg-transparent rounded-sm px-2 md:px-0 md:pt-4 md:hidden">
          <Breadcrumb items={items} />
        </div>
      </section>
      <LocalPageHeader {...headerProps} items={items} />
      <div className="flex-grow">
        <DriverListClient
          profiles={profiles}
          filterStats={filterStats}
          currentPage={currentPage}
          totalPages={totalPages}
          searchParams={searchParams}
          totalDrivers={totalDrivers}
          itemsPerPage={itemsPerPage}
        />
      </div>
      <Footer noBlok />
    </ReviewsContextProvider>
  )
}
